import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { api, assetUrl, formatDate, type Course, type CourseEdition, type CourseResource } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import { InscripcionFromEdition } from "@/components/feature/InscripcionModal";

export default function CursoPublicPage() {
  const branding = useBranding();
  const { id } = useParams<{ id: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEdition, setSelectedEdition] = useState<CourseEdition | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getPublicCourse(id).then(setCourse).catch(() => setCourse(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <i className="ri-loader-4-line animate-spin text-3xl text-[var(--color-accent-oil)]"></i>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
        <PublicNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-[var(--color-text-muted)]">Curso no encontrado</p>
            <Link to="/" className="btn-primary mt-4 inline-block">Inicio</Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const courseTitle = course.detail_title || course.name;
  const courseImage = branding.courseImage(course.image_url);
  const highlights = course.highlights || [];
  const resources = (course.resource_files || []).filter((resource: CourseResource) => resource?.url);
  const editions = course.editions ?? [];

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
      <PageMeta
        title={`${course.name} | ${branding.site_name}`}
        description={course.description.slice(0, 160)}
        path={`/curso/${course.id}`}
        image={courseImage}
      />
      <PublicNavbar />
      <div className="flex-1 pt-[88px] pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <section className="relative w-full overflow-hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] aspect-[16/9]">
            <img
              src={courseImage}
              alt={course.name}
              sizes="(min-width: 1280px) 1180px, 100vw"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080f1a]/80 via-[#080f1a]/15 to-transparent" />
            <div className="absolute left-4 sm:left-6 right-4 sm:right-6 bottom-4 sm:bottom-6 flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-accent-oil)] text-[#080f1a]">{course.category}</span>
              {course.modality && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20">{course.modality}</span>}
            </div>
          </section>

          <div className="grid xl:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-8 items-start">
            <main className="space-y-6 min-w-0">
              <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 lg:p-8 min-w-0">
                <span className="text-xs font-semibold text-[var(--color-accent-oil)] uppercase tracking-[0.18em]">Detalle del curso</span>
                <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold text-[var(--color-text-primary)] mt-3 leading-[1.04] break-words">{courseTitle}</h1>
                {courseTitle !== course.name && <p className="text-base sm:text-lg font-semibold text-[var(--color-text-secondary)] mt-3">{course.name}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-6">
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-3 sm:p-4">
                    <i className="ri-time-line text-[var(--color-accent-oil)]"></i>
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-2">Duración</p>
                    <p className="text-sm sm:text-base font-bold truncate">{course.duration || `${course.hours}h`}</p>
                  </div>
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-3 sm:p-4">
                    <i className="ri-hourglass-line text-[var(--color-accent-oil)]"></i>
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-2">Horas</p>
                    <p className="text-sm sm:text-base font-bold">{course.hours}h</p>
                  </div>
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-3 sm:p-4">
                    <i className="ri-computer-line text-[var(--color-accent-oil)]"></i>
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-2">Modalidad</p>
                    <p className="text-sm sm:text-base font-bold truncate">{course.modality || "Por definir"}</p>
                  </div>
                </div>
              </section>

            <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]">
                  <i className="ri-book-open-line"></i>
                </div>
                <h2 className="text-xl font-bold">Contenido del curso</h2>
              </div>
              <p className="text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-line">{course.description}</p>
            </section>

            <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]">
                    <i className="ri-sparkling-line"></i>
                  </div>
                  <h2 className="text-xl font-bold">Aspectos importantes</h2>
                </div>
                {highlights.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {highlights.map((item) => (
                      <div key={item} className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 flex gap-3">
                        <i className="ri-check-double-line text-[var(--color-accent-oil)] mt-0.5"></i>
                        <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[var(--color-text-muted)] text-sm">Agrega puntos destacados desde la edición del curso para mostrarlos aquí.</p>
                )}
            </section>

            <section className="xl:hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]">
                  <i className="ri-user-star-line"></i>
                </div>
                <h2 className="text-xl font-bold">A quién va dirigido</h2>
              </div>
              <p className="text-[var(--color-text-secondary)] leading-relaxed">
                {course.target_audience || "Personal operativo, supervisores, responsables de seguridad y equipos técnicos que requieran fortalecer sus competencias."}
              </p>
            </section>

            {resources.length > 0 && (
              <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
                  <div>
                    <h2 className="text-xl font-bold">Recursos descargables</h2>
                    <p className="text-sm text-[var(--color-text-muted)] mt-1">Materiales, guías o archivos complementarios del curso.</p>
                  </div>
                  <i className="ri-download-cloud-2-line text-3xl text-[var(--color-accent-oil)]"></i>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {resources.map((resource) => {
                    const href = assetUrl(resource.url) || resource.url;
                    return (
                      <a
                        key={`${resource.name}-${resource.url}`}
                        href={href}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 flex items-center justify-between gap-4 hover:border-[var(--color-accent-oil)]/50 transition-colors"
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <i className="ri-file-download-line text-[var(--color-accent-oil)] text-xl"></i>
                          <span className="font-semibold text-sm truncate">{resource.name || "Descargar recurso"}</span>
                        </span>
                        <i className="ri-arrow-right-line text-[var(--color-text-muted)]"></i>
                      </a>
                    );
                  })}
                </div>
              </section>
            )}
          </main>

          <aside className="xl:sticky xl:top-24 order-first xl:order-none">
            <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 shadow-2xl shadow-black/20">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <span className="text-xs font-semibold text-[var(--color-accent-oil)] uppercase tracking-[0.16em]">Inscripción</span>
                  <h2 className="text-2xl font-bold mt-1">Elige una edición</h2>
                </div>
                <div className="w-11 h-11 rounded-2xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]">
                  <i className="ri-user-add-line text-xl"></i>
                </div>
              </div>

              {editions.length === 0 ? (
                <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 text-sm text-[var(--color-text-secondary)]">
                  <p className="font-semibold text-[var(--color-text-primary)]">No hay ediciones abiertas por ahora.</p>
                  <p className="mt-2">Cuando se publique una fecha podrás inscribirte desde este mismo panel.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {editions.map((ed) => {
                    const enrolled = ed._count?.enrollments || 0;
                    const available = Math.max(ed.capacity - enrolled, 0);
                    return (
                      <div key={ed.id} className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-[var(--color-text-primary)] leading-snug">{ed.edition_label || formatDate(ed.start_date)}</p>
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                              {formatDate(ed.start_date)} - {formatDate(ed.end_date)}
                            </p>
                          </div>
                          <span className="text-sm font-bold text-[var(--color-accent-oil)] whitespace-nowrap">${ed.price.toLocaleString("en-US")}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-[var(--color-text-muted)]">
                          <span className="flex items-center gap-1 min-w-0">
                            <i className="ri-map-pin-line text-[var(--color-accent-oil)]"></i>
                            <span className="truncate">{ed.location || "Sede por definir"}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-group-line text-[var(--color-accent-oil)]"></i>
                            {available} cupos
                          </span>
                        </div>
                        {ed.instructor && (
                          <p className="text-xs text-[var(--color-text-muted)] mt-2">Instructor: {ed.instructor.full_name}</p>
                        )}
                        <button onClick={() => setSelectedEdition({ ...ed, course })} className="btn-primary w-full mt-4 cursor-pointer">
                          Inscribirme
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="hidden xl:block rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 mt-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]">
                  <i className="ri-user-star-line"></i>
                </div>
                <h2 className="text-xl font-bold">A quién va dirigido</h2>
              </div>
              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                {course.target_audience || "Personal operativo, supervisores, responsables de seguridad y equipos técnicos que requieran fortalecer sus competencias."}
              </p>
            </div>
          </aside>
        </div>
        </div>
      </div>
      <PublicFooter />

      {selectedEdition && (
        <InscripcionFromEdition edition={selectedEdition} onClose={() => setSelectedEdition(null)} />
      )}
    </div>
  );
}
